import {
	ObservableMap,
	ObservableSet,
	action,
	computed,
	observable,
} from "mobx";

import { LoaderStore } from "@state/stores/common";
import QuickPlayStore from "./QuickPlayStore";

import { TMostPlayedTicket } from "@data-types/bet-booster/MostPlayedTickets";
import { BettingOffer } from "@models";

export default class MostPlayTicketsStore {
	quickPlayStore: QuickPlayStore;
	loader: LoaderStore;
	betSlipIndex: number;

	@observable public selectedSportCategory: string | null = null;

	@observable betSlips = new ObservableMap<string, TMappedBetSlip>();
	@observable sportCategories: TSportCategories[] = observable.array([]);

	@observable isStoreInitialized: boolean = false;
	@observable isFilteredList: boolean = false;
	@observable public isPopupDisplayed: boolean = false;

	@observable public selectedBetSlip: TMappedBetSlip | null = null;
	@observable public filteredBetSlips = new ObservableMap<
		string,
		TMappedBetSlip
	>();
	@observable public isAddingOrRemovingPick: boolean = false;

	constructor(quickPlayStore: QuickPlayStore) {
		this.loader = new LoaderStore();
		this.quickPlayStore = quickPlayStore;
	}

	@computed get isInBetSlip(): ObservableSet {
		const activeBetSlips = new ObservableSet();

		const betSlip =
			this.quickPlayStore.rootStore.betSlipStore.betSlipState.slip;

		this.currentActiveList.forEach((bet) => {
			// offer ids on most played ticket
			const offerIds = bet.betSlipOffers.map(
				(offer) => offer.bettingOfferId
			);

			// offer ids on active bet slip
			const currentOfferIds = betSlip.betSlipOffers.map(
				(offer) => offer.bettingOfferId
			);

			const set1 = new Set(offerIds);
			const set2 = new Set(currentOfferIds);

			for (const item of set1) {
				if (!set2.has(item)) {
					return;
				}
			}

			activeBetSlips.add(bet.betSlipId);
		});

		return activeBetSlips;
	}

	@computed get isLoading() {
		return this.loader.isLoading;
	}

	@computed get currentActiveList() {
		return this.isFilteredList ? this.filteredBetSlips : this.betSlips;
	}

	//#region  popup
	@action.bound
	activatePopup(id: string, index: number, isFilteredList: boolean) {
		this.isFilteredList = isFilteredList;
		this.isPopupDisplayed = true;
		this.betSlipIndex = index;
		const slip = this.currentActiveList.get(id);
		if (slip) {
			this.selectedBetSlip = slip;
		}
	}

	@action.bound
	deactivatePopup() {
		this.isPopupDisplayed = false;
		this.isFilteredList = false;
		this.selectedBetSlip = null;
	}

	@action.bound
	changePage(num: ChangePageButton) {
		const betslips = this.isFilteredList
			? this.filteredBetSlips
			: this.betSlips;
		const betslipsArray = Array.from(betslips);
		const totalSize = betslipsArray.length;

		let currentIndex = betslipsArray.findIndex(
			([key]) => key === this.selectedBetSlip?.betSlipId
		);

		switch (num) {
			case ChangePageButton.PREVIOUS:
				if (currentIndex > 0) {
					currentIndex -= 1;
				} else {
					currentIndex = totalSize - 1;
				}
				break;
			case ChangePageButton.NEXT:
				if (currentIndex < totalSize - 1) {
					currentIndex += 1;
				} else {
					currentIndex = 0;
				}
				break;
			default:
				currentIndex = num;
				break;
		}

		this.selectedBetSlip = betslipsArray[currentIndex][1];
		this.betSlipIndex = currentIndex + 1;
	}

	//#endregion popup

	@action.bound
	setIsAddingOrRemovingPick(value: boolean) {
		this.isAddingOrRemovingPick = value;
	}

	@action.bound
	addOrRemoveMostPlayedTicket(betSlip: TMappedBetSlip) {
		this.setIsAddingOrRemovingPick(true);

		if (this.isInBetSlip.has(betSlip.betSlipId)) {
			betSlip.betSlipOffers.map((offer) => {
				this.quickPlayStore.rootStore.betSlipStore.betSlipState.removeFromBetSlip(
					{
						id: offer.bettingOfferId,
					}
				);
			});
		} else {
			this.quickPlayStore.rootStore.betSlipStore.useBetSlip({
				...betSlip,
			});
		}

		this.setIsAddingOrRemovingPick(false);
	}

	@action.bound
	public onInitializeMostPlayTickets() {
		this.mapMostPlayedTickets();
		this.isStoreInitialized = true;
	}

	@action.bound
	private createBetslipWithMappedBettingOffers(
		betslips: Map<string, TMostPlayedTicket>
	) {
		const mappedBettingOffer = new ObservableMap<string, TMappedBetSlip>();
		this.quickPlayStore.betBoosterStore.hubData.mostPlayedTicketIds.forEach(
			(bsId) => {
				const betslip = betslips.get(bsId);
				if (betslip) {
					const mappedBetSlipOffers = betslip.betSlipOffers.reduce(
						(acc: BettingOffer[], betSlipOffer) => {
							acc.push(new BettingOffer(betSlipOffer));
							return acc;
						},
						[]
					);
					mappedBettingOffer.set(bsId, {
						...betslip,
						betSlipOffers: mappedBetSlipOffers,
					});
				}
			}
		);
		return mappedBettingOffer;
	}

	@action.bound
	updateMostPlayedTicket(slip: TMostPlayedTicket) {
		const bbSlip = this.betSlips.get(slip.betSlipId);
		if (bbSlip) {
			bbSlip.quotaIndicator = slip.quotaIndicator;
			bbSlip.submitCountIndicator = slip.submitCountIndicator;
		}
	}

	@action.bound
	private createBetSlipsSportsCategories() {
		const sportCategoriesSet = new Map<string, TSportCategories>();
		this.betSlips.forEach((betslip) => {
			if (!sportCategoriesSet.has(betslip.sportAbrv)) {
				sportCategoriesSet.set(betslip.sportAbrv, {
					sportAbrv: betslip.sportAbrv,
					sortOrder: betslip.sportSortOrder || 9999,
					sportName: betslip.sportName,
				});
			}
		});

		const sortedSportCategories = [...sportCategoriesSet.values()].sort(
			(a, b) => a.sortOrder - b.sortOrder
		);

		this.sportCategories = sortedSportCategories;
	}
	public setSelectedSportCategory(sportAbrv: string) {
		this.selectedSportCategory = sportAbrv;
	}

	@action.bound
	public filterByCategory(sportAbrv: string | null) {
		if (!sportAbrv) {
			this.filteredBetSlips = this.betSlips;
			return;
		}
		this.filteredBetSlips.clear();
		this.quickPlayStore.betBoosterStore.hubData.mostPlayedTicketIds.forEach(
			(bsId) => {
				const bbPick = this.betSlips.get(bsId);
				if (!bbPick) {
					return;
				}

				if (bbPick.sportAbrv == sportAbrv) {
					this.filteredBetSlips.set(bbPick.betSlipId, bbPick);
				}
			}
		);
		this.selectedSportCategory = sportAbrv;
	}

	@action.bound
	public mapMostPlayedTickets() {
		if (
			!this.quickPlayStore.betBoosterStore.hubData?.mostPlayedTickets
				?.size
		) {
			console.error("no bet slips");
			return;
		}

		const mappedBetSlips = this.createBetslipWithMappedBettingOffers(
			this.quickPlayStore.betBoosterStore.hubData?.mostPlayedTickets
		);

		if (this.selectedBetSlip) {
			if (
				this.quickPlayStore.betBoosterStore.hubData.mostPlayedTicketIds.find(
					(id) => id == this.selectedBetSlip?.betSlipId
				)
			) {
				const slip = this.betSlips.get(this.selectedBetSlip?.betSlipId);
				if (slip) {
					this.selectedBetSlip = slip;
				}
			}
		}
		this.betSlips = mappedBetSlips;

		this.createBetSlipsSportsCategories();
		if (
			!this.selectedSportCategory ||
			!this.sportCategories.some(
				(cat) => this.selectedSportCategory == cat.sportAbrv
			)
		) {
			this.setSelectedSportCategory(this.sportCategories[0]?.sportAbrv);
		}
		this.filterByCategory(this.selectedSportCategory);
	}

	@action.bound
	onDispose() {
		this.isStoreInitialized = false;
		this.betSlips.clear();
		this.sportCategories = [];
		this.filteredBetSlips.clear();
		this.isFilteredList = false;
		this.isPopupDisplayed = false;
	}
}
export type TMappedBetSlip = Omit<TMostPlayedTicket, "betSlipOffers"> & {
	betSlipOffers: BettingOffer[];
};

export enum ChangePageButton {
	"PREVIOUS",
	"NEXT",
}

type TSportCategories = {
	sportAbrv: string;
	sortOrder: number;
	sportName: string;
};
